import {
  STTOK_LINK_PROTOCOL,
  getAiPanelRoute,
} from 'modules/ai/constants/aiPanelRoutes';

const DEFAULT_LINK_PROTOCOLS = ['http', 'https', 'mailto', 'tel'];

/**
 * Mirror of react-markdown uriTransformer, plus sttok:// (unknown protocols become javascript:void(0)).
 */
const defaultMarkdownUriTransform = (uri: string): string => {
  const url = (uri || '').trim();
  const first = url.charAt(0);

  if (first === '#' || first === '/') {
    return url;
  }

  const colon = url.indexOf(':');
  if (colon === -1) {
    return url;
  }

  const protocolMatch = DEFAULT_LINK_PROTOCOLS.some(
    (protocol) =>
      colon === protocol.length &&
      url.slice(0, protocol.length).toLowerCase() === protocol
  );
  if (protocolMatch) {
    return url;
  }

  const queryIndex = url.indexOf('?');
  if (queryIndex !== -1 && colon > queryIndex) {
    return url;
  }

  const hashIndex = url.indexOf('#');
  if (hashIndex !== -1 && colon > hashIndex) {
    return url;
  }

  return 'javascript:void(0)'; // eslint-disable-line
};

const LEGACY_PANEL_LINK_PREFIX_PATTERNS = [
  /\*\*En el panel:\*\*\s*/gi,
  /\*\*In the panel:\*\*\s*/gi,
  /\*\*Dans le panneau\s*:\*\*\s*/gi,
];

/** Removes legacy "En el panel:" bold prefix from AI markdown (older responses). */
export const stripLegacyPanelLinkPrefix = (text: string): string => {
  if (!text) return text;
  return LEGACY_PANEL_LINK_PREFIX_PATTERNS.reduce(
    (acc, pattern) => acc.replace(pattern, ''),
    text
  );
};

/** Preserve sttok:// URIs; react-markdown otherwise replaces them with javascript:void(0). */
export const transformAiMarkdownLinkUri = (uri: string): string => {
  const url = (uri || '').trim();
  if (url.startsWith(STTOK_LINK_PROTOCOL)) {
    return url;
  }
  return defaultMarkdownUriTransform(url);
};

export type SttokLinkParams = {
  societyId: string;
  extraParams?: Record<string, string>;
};

export const parseSttokHref = (
  href: string | undefined
): { routeId: string } | null => {
  if (!href || !href.startsWith(STTOK_LINK_PROTOCOL)) {
    return null;
  }
  const routeId = href.slice(STTOK_LINK_PROTOCOL.length).trim().toLowerCase();
  if (!routeId || !getAiPanelRoute(routeId)) {
    return null;
  }
  return { routeId };
};

export const buildPanelPath = (
  routeId: string,
  { societyId, extraParams = {} }: SttokLinkParams
): string | null => {
  const route = getAiPanelRoute(routeId);
  if (!route || !societyId) {
    return null;
  }

  let path = route.pathTemplate.replace(':societyId', societyId);

  Object.entries(extraParams).forEach(([key, value]) => {
    path = path.replace(`:${key}`, value);
  });

  if (path.includes(':')) {
    return null;
  }

  return path;
};

export const isSttokPanelHref = (href: string | undefined): boolean =>
  parseSttokHref(href) !== null;
